import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";

import { getRouter } from "./router";
import { registerPushServiceWorker } from "./lib/push";
import { setupInstallAppPrompt } from "./lib/install-app";
import "./styles.css";
import "./shared-platform-theme.css";
import "./inanna-offer-theme.css";
import "./auth-confirmation-notice.css";

const router = getRouter();

const isInannaOffer =
  window.location.hostname === "codigodeinanna.dimmedia.net" &&
  (window.location.pathname === "/oferta" || window.location.pathname === "/oferta/");

if (isInannaOffer) {
  document.documentElement.dataset["inannaPalette"] = "c";
} else {
  delete document.documentElement.dataset["inannaPalette"];
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Não foi possível iniciar o aplicativo.");
}

const LAST_TAB_KEY = "100codigos:last-tab";
const TAB_LABELS: Record<string, string[]> = {
  home: ["Início"],
  today: ["Hoje"],
  codes: ["Códigos"],
  map: ["Mapa", "Meu Mapa"],
  profile: ["Perfil"],
};

let pendingTodaySearch: string | null = null;
let restoredTab = false;

function polishCodeDetail() {
  const headings = Array.from(document.querySelectorAll("h2"));
  const instructionsHeading = headings.find(
    (heading) => heading.textContent?.trim() === "Seu direcionamento",
  );
  const sequenceHeading = headings.find((heading) => heading.textContent?.trim() === "Sequência");
  const ritualHeading = headings.find((heading) => heading.textContent?.trim() === "Ritual");

  if (!instructionsHeading || !sequenceHeading) return;

  const section = instructionsHeading.closest("section");
  if (!section || sequenceHeading.closest("section") !== section) return;

  const instructionsBlock = instructionsHeading.parentElement;
  const sequenceBlock = sequenceHeading.parentElement;
  const ritualBlock = ritualHeading?.closest("section") === section ? ritualHeading.parentElement : null;
  const affirmation = section.querySelector("blockquote");
  const frequencyStrong = Array.from(section.querySelectorAll("strong")).find((strong) =>
    strong.textContent?.trim().startsWith("Frequência:"),
  );
  const frequencyBlock = frequencyStrong?.parentElement ?? null;
  const actionButton = Array.from(section.querySelectorAll("button")).find((button) =>
    button.textContent?.includes("COMEÇAR MEU CICLO DE 3 DIAS"),
  );

  instructionsHeading.textContent = "Como realizar o ritual";
  if (ritualBlock) ritualBlock.style.display = "none";
  if (frequencyStrong) {
    frequencyStrong.textContent = "Frequência sugerida para ouvir durante o ritual: ";
  }

  section.style.display = "flex";
  section.style.flexDirection = "column";

  Array.from(section.children).forEach((child, index) => {
    (child as HTMLElement).style.order = String(index);
  });

  if (sequenceBlock) sequenceBlock.style.order = "30";
  if (affirmation instanceof HTMLElement) affirmation.style.order = "40";
  if (instructionsBlock) instructionsBlock.style.order = "50";
  if (frequencyBlock) frequencyBlock.style.order = "60";
  if (actionButton) actionButton.style.order = "70";
}

function findNavButton(labels: string[]) {
  return Array.from(document.querySelectorAll("button")).find((button) =>
    labels.includes(button.textContent?.trim() ?? ""),
  );
}

function rememberCurrentTab(event: Event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest("button");
  if (!button) return;
  const text = button.textContent?.trim() ?? "";

  for (const [tab, labels] of Object.entries(TAB_LABELS)) {
    if (labels.includes(text)) {
      window.localStorage.setItem(LAST_TAB_KEY, tab);
      break;
    }
  }
}

function restoreLastTab() {
  if (restoredTab) return;
  const saved = window.localStorage.getItem(LAST_TAB_KEY);
  const labels = saved ? TAB_LABELS[saved] : undefined;
  if (!saved || saved === "home" || !labels) {
    restoredTab = true;
    return;
  }

  const target = findNavButton(labels);
  if (!target) return;
  restoredTab = true;
  target.click();
}

function removeTodayFeelingSearch() {
  document.querySelectorAll("[data-today-feeling-search]").forEach((node) => node.remove());
}

function addTodayFeelingSearch() {
  const dailyLabel = Array.from(document.querySelectorAll("p")).find(
    (node) => node.textContent?.trim() === "Seu cuidado diário",
  );

  if (!dailyLabel) {
    removeTodayFeelingSearch();
    return;
  }

  const main = dailyLabel.closest("main") ?? dailyLabel.parentElement?.parentElement;
  if (!main || main.querySelector("[data-today-feeling-search]")) return;

  const cycleLabel = Array.from(main.querySelectorAll("p")).find(
    (node) => node.textContent?.trim() === "Seu ciclo em andamento",
  );
  const cycleSection = cycleLabel?.closest("section") ?? null;
  if (!cycleSection) return;

  const form = document.createElement("form");
  form.dataset["todayFeelingSearch"] = "true";
  form.className = "mt-5 rounded-[24px] border bg-white p-5";
  form.style.boxShadow = "var(--shadow-soft)";
  form.innerHTML = `
    <p class="text-xs font-bold uppercase tracking-[0.13em] text-berry">Está sentindo algo diferente hoje?</p>
    <p class="mt-2 text-[15px] leading-relaxed text-text/70">Escreva em poucas palavras como você está se sentindo.</p>
    <div class="mt-4 flex items-center gap-3 rounded-2xl border bg-cream/40 px-4 py-3 focus-within:border-berry">
      <span aria-hidden="true" class="text-berry text-lg">⌕</span>
      <input data-today-feeling-input class="w-full bg-transparent text-[15px] outline-none" placeholder="Ex.: ele não me responde, estou com ciúmes..." />
    </div>
    <button type="submit" class="mt-3 min-h-[48px] w-full rounded-full bg-berry px-5 py-3 text-sm font-bold text-white">ENCONTRAR MEU CÓDIGO</button>
  `;

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = form.querySelector<HTMLInputElement>("[data-today-feeling-input]");
    const value = input?.value.trim() ?? "";
    if (!value) {
      input?.focus();
      return;
    }

    pendingTodaySearch = value;
    form.remove();
    window.localStorage.setItem(LAST_TAB_KEY, "codes");
    findNavButton(TAB_LABELS["codes"] ?? [])?.click();
  });

  cycleSection.parentElement?.insertBefore(form, cycleSection);
}

function applyPendingTodaySearch() {
  if (!pendingTodaySearch) return;

  const librarySearch = document.querySelector<HTMLInputElement>(
    'input[placeholder="Ex.: desapego, confiança, reciprocidade..."]',
  );
  if (!librarySearch) return;

  const value = pendingTodaySearch;
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  setter?.call(librarySearch, value);
  librarySearch.dispatchEvent(new Event("input", { bubbles: true }));

  const searchContainer = librarySearch.parentElement;
  if (!searchContainer) return;
  searchContainer.style.display = "none";

  document.querySelectorAll("[data-today-search-result]").forEach((node) => node.remove());

  const resultsLabel = document.createElement("div");
  resultsLabel.dataset["todaySearchResult"] = "true";
  resultsLabel.className =
    "mb-4 flex items-center justify-between gap-3 rounded-2xl bg-light-rose/35 px-4 py-3";
  resultsLabel.innerHTML = `
    <div>
      <p class="text-xs font-bold uppercase tracking-[0.12em] text-berry">Resultados para</p>
      <p data-query-label class="mt-1 text-[15px] font-semibold text-wine"></p>
    </div>
    <button type="button" class="shrink-0 text-sm font-bold text-berry">Nova busca</button>
  `;

  const queryLabel = resultsLabel.querySelector("[data-query-label]");
  if (queryLabel) queryLabel.textContent = `“${value}”`;

  resultsLabel.querySelector("button")?.addEventListener("click", () => {
    resultsLabel.remove();
    searchContainer.style.display = "";
    setter?.call(librarySearch, "");
    librarySearch.dispatchEvent(new Event("input", { bubbles: true }));
    librarySearch.focus();
  });

  searchContainer.parentElement?.insertBefore(resultsLabel, searchContainer.nextSibling);
  pendingTodaySearch = null;
}

function cleanTransferredSearchOutsideLibrary() {
  const libraryLabel = Array.from(document.querySelectorAll("p")).find(
    (node) => node.textContent?.trim() === "Sua biblioteca",
  );
  if (libraryLabel) return;
  document.querySelectorAll("[data-today-search-result]").forEach((node) => node.remove());
}

const uiObserver = new MutationObserver(() => {
  polishCodeDetail();
  addTodayFeelingSearch();
  applyPendingTodaySearch();
  cleanTransferredSearchOutsideLibrary();
  restoreLastTab();
});
uiObserver.observe(rootElement, { childList: true, subtree: true });
rootElement.addEventListener("click", rememberCurrentTab, true);

setupInstallAppPrompt();

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

window.setTimeout(() => {
  polishCodeDetail();
  addTodayFeelingSearch();
  applyPendingTodaySearch();
  restoreLastTab();
}, 0);

void registerPushServiceWorker().catch((error: unknown) => {
  console.warn("[Push] Não foi possível atualizar o service worker.", error);
});
